import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    contractorsApi,
    BusinessInfoRequest,
    BusinessInfoResponse,
    ClassificationRequest,
    ClassificationResponse,
    ContactInfoRequest,
    ContactInfoResponse,
    AddTeamMembersRequest,
    AddTeamMembersResponse,
    ArchitectResponse,
    SubcontractorResponse
} from '@/services/contractors';
import { CompleteOnboardingResponse, ContractorOnboardingStatusResponse } from '@/types/api';

// Hook for fetching architect data
export const useGetArchitect = (architectId: string | null) => {
    return useQuery<ArchitectResponse, Error>({
        queryKey: ['architect', architectId],
        queryFn: () => contractorsApi.getArchitect(architectId!),
        enabled: !!architectId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
    });
};

// Hook for fetching subcontractor data
export const useGetSubcontractor = (subcontractorId: string | null) => {
    return useQuery<SubcontractorResponse, Error>({
        queryKey: ['subcontractor', subcontractorId],
        queryFn: () => contractorsApi.getSubcontractor(subcontractorId!),
        enabled: !!subcontractorId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 3,
    });
};

// Hook for updating architect business info
export const useUpdateArchitectBusinessInfo = () => {
    const queryClient = useQueryClient();

    return useMutation<BusinessInfoResponse, Error, { architectId: string; data: BusinessInfoRequest }>({
        mutationFn: ({ architectId, data }) => contractorsApi.updateArchitectBusinessInfo(architectId, data),
        onSuccess: (data, variables) => {
            console.log('Architect business info update successful:', data);
            // Invalidate and refetch architect data
            queryClient.invalidateQueries({ queryKey: ['architect', variables.architectId] });
        },
        onError: (error) => {
            console.error('Failed to update architect business info:', error);
        },
    });
};

// Hook for updating subcontractor business info
export const useUpdateSubcontractorBusinessInfo = () => {
    const queryClient = useQueryClient();

    return useMutation<BusinessInfoResponse, Error, { subcontractorId: string; data: BusinessInfoRequest }>({
        mutationFn: ({ subcontractorId, data }) => contractorsApi.updateSubcontractorBusinessInfo(subcontractorId, data),
        onSuccess: (data, variables) => {
            console.log('Subcontractor business info update successful:', data);
            // Invalidate and refetch subcontractor data
            queryClient.invalidateQueries({ queryKey: ['subcontractor', variables.subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to update subcontractor business info:', error);
        },
    });
};

// Hook for updating architect classification
export const useUpdateArchitectClassification = () => {
    const queryClient = useQueryClient();

    return useMutation<ClassificationResponse, Error, { architectId: string; data: ClassificationRequest }>({
        mutationFn: ({ architectId, data }) => contractorsApi.updateArchitectClassification(architectId, data),
        onSuccess: (data, variables) => {
            console.log('Architect classification update successful:', data);
            // Invalidate and refetch architect data
            queryClient.invalidateQueries({ queryKey: ['architect', variables.architectId] });
        },
        onError: (error) => {
            console.error('Failed to update architect classification:', error);
        },
    });
};

// Hook for updating subcontractor classification
export const useUpdateSubcontractorClassification = () => {
    const queryClient = useQueryClient();

    return useMutation<ClassificationResponse, Error, { subcontractorId: string; data: ClassificationRequest }>({
        mutationFn: ({ subcontractorId, data }) => contractorsApi.updateSubcontractorClassification(subcontractorId, data),
        onSuccess: (data, variables) => {
            console.log('Subcontractor classification update successful:', data);
            // Invalidate and refetch subcontractor data
            queryClient.invalidateQueries({ queryKey: ['subcontractor', variables.subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to update subcontractor classification:', error);
        },
    });
};

// Hook for updating architect contact info
export const useUpdateArchitectContactInfo = () => {
    const queryClient = useQueryClient();

    return useMutation<ContactInfoResponse, Error, { architectId: string; data: ContactInfoRequest }>({
        mutationFn: ({ architectId, data }) => contractorsApi.updateArchitectContactInfo(architectId, data),
        onSuccess: (data, variables) => {
            console.log('Architect contact info update successful:', data);
            // Invalidate and refetch architect data
            queryClient.invalidateQueries({ queryKey: ['architect', variables.architectId] });
        },
        onError: (error) => {
            console.error('Failed to update architect contact info:', error);
        },
    });
};

// Hook for updating subcontractor contact info
export const useUpdateSubcontractorContactInfo = () => {
    const queryClient = useQueryClient();

    return useMutation<ContactInfoResponse, Error, { subcontractorId: string; data: ContactInfoRequest }>({
        mutationFn: ({ subcontractorId, data }) => contractorsApi.updateSubcontractorContactInfo(subcontractorId, data),
        onSuccess: (data, variables) => {
            console.log('Subcontractor contact info update successful:', data);
            // Invalidate and refetch subcontractor data
            queryClient.invalidateQueries({ queryKey: ['subcontractor', variables.subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to update subcontractor contact info:', error);
        },
    });
};

// Hook for adding architect team members
export const useAddArchitectTeamMembers = () => {
    const queryClient = useQueryClient();

    return useMutation<AddTeamMembersResponse, Error, { architectId: string; data: AddTeamMembersRequest }>({
        mutationFn: ({ architectId, data }) => contractorsApi.addArchitectTeamMembers(architectId, data),
        onSuccess: (data, variables) => {
            console.log('Architect team members added successfully:', data);
            // Invalidate and refetch architect data
            queryClient.invalidateQueries({ queryKey: ['architect', variables.architectId] });
        },
        onError: (error) => {
            console.error('Failed to add architect team members:', error);
        },
    });
};

// Hook for adding subcontractor team members
export const useAddSubcontractorTeamMembers = () => {
    const queryClient = useQueryClient();

    return useMutation<AddTeamMembersResponse, Error, { subcontractorId: string; data: AddTeamMembersRequest }>({
        mutationFn: ({ subcontractorId, data }) => contractorsApi.addSubcontractorTeamMembers(subcontractorId, data),
        onSuccess: (data, variables) => {
            console.log('Subcontractor team members added successfully:', data);
            // Invalidate and refetch subcontractor data
            queryClient.invalidateQueries({ queryKey: ['subcontractor', variables.subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to add subcontractor team members:', error);
        },
    });
};

// Hook for completing architect onboarding
export const useCompleteArchitectOnboarding = () => {
    const queryClient = useQueryClient();

    return useMutation<CompleteOnboardingResponse, Error, string>({
        mutationFn: (architectId: string) => contractorsApi.completeArchitectOnboarding(architectId),
        onSuccess: (data, architectId) => {
            console.log('Architect onboarding completed successfully:', data);
            // Invalidate architect data to refetch updated status
            queryClient.invalidateQueries({ queryKey: ['architect', architectId] });
        },
        onError: (error) => {
            console.error('Failed to complete architect onboarding:', error);
        }
    });
};

// Hook for completing subcontractor onboarding
export const useCompleteSubcontractorOnboarding = () => {
    const queryClient = useQueryClient();

    return useMutation<CompleteOnboardingResponse, Error, string>({
        mutationFn: (subcontractorId: string) => contractorsApi.completeSubcontractorOnboarding(subcontractorId),
        onSuccess: (data, subcontractorId) => {
            console.log('Subcontractor onboarding completed successfully:', data);
            // Invalidate subcontractor data to refetch updated status
            queryClient.invalidateQueries({ queryKey: ['subcontractor', subcontractorId] });
        },
        onError: (error) => {
            console.error('Failed to complete subcontractor onboarding:', error);
        }
    });
};

// Hook for getting architect onboarding status
export const useGetArchitectOnboardingStatus = (architectId: string | null) => {
    return useQuery<ContractorOnboardingStatusResponse, Error>({
        queryKey: ['architect-onboarding-status', architectId],
        queryFn: () => contractorsApi.getArchitectOnboardingStatus(architectId!),
        enabled: !!architectId,
        staleTime: 30 * 1000, // 30 seconds - we want fresh data for onboarding status
        retry: 3,
    });
};

// Hook for getting subcontractor onboarding status
export const useGetSubcontractorOnboardingStatus = (subcontractorId: string | null) => {
    return useQuery<ContractorOnboardingStatusResponse, Error>({
        queryKey: ['subcontractor-onboarding-status', subcontractorId],
        queryFn: () => contractorsApi.getSubcontractorOnboardingStatus(subcontractorId!),
        enabled: !!subcontractorId,
        staleTime: 30 * 1000, // 30 seconds - we want fresh data for onboarding status
        retry: 3,
    });
};
